import type { Percents, DoughResult } from "../types";

/* Commercial yeast / simple percentages. */
export function computeStandard(totalDough: number, p: Percents): DoughResult {
  const H = p.hydration / 100, S = p.salt / 100, O = p.oil / 100;
  const Su = p.sugar / 100, B = (p.butter || 0) / 100, Y = p.yeast / 100;
  const flour = totalDough / (1 + H + S + O + Su + B + Y);
  return {
    flour,
    rows: [
      { name: "Flour", weight: flour, pct: 100, accent: true },
      { name: "Water", weight: flour * H, pct: p.hydration, accent: true },
      { name: "Salt", weight: flour * S, pct: p.salt },
      p.oil > 0 ? { name: "Oil", weight: flour * O, pct: p.oil } : null,
      p.sugar > 0 ? { name: "Sugar", weight: flour * Su, pct: p.sugar } : null,
      (p.butter || 0) > 0 ? { name: "Butter", weight: flour * B, pct: p.butter } : null,
      { name: "Yeast", weight: flour * Y, pct: p.yeast },
    ],
  };
}

/* Advanced sourdough: starter contributes flour + water; standard denominator
   keeps total dough == target (deliberate deviation from spec 4.2). */
export function computeAdvancedSourdough(totalDough: number, p: Percents): DoughResult {
  const H = p.hydration / 100, S = p.salt / 100, O = p.oil / 100;
  const Su = p.sugar / 100, B = (p.butter || 0) / 100;
  const St = p.yeast / 100, Sh = p.starterHyd / 100;
  const flour = totalDough / (1 + H + S + O + Su + B);
  const starterFlour = flour * St;
  const starterWater = flour * St * Sh;
  const starterWeight = starterFlour + starterWater;
  const finalFlour = flour - starterFlour;
  const finalWater = flour * H - starterWater;
  return {
    flour,
    finalWater,
    rows: [
      { name: "Flour (add)", weight: finalFlour, pct: null, accent: true },
      { name: "Water (add)", weight: finalWater, pct: null, accent: true },
      { name: "Starter", weight: starterWeight, pct: p.yeast, accent: true },
      { name: "Salt", weight: flour * S, pct: p.salt },
      p.oil > 0 ? { name: "Oil", weight: flour * O, pct: p.oil } : null,
      p.sugar > 0 ? { name: "Sugar", weight: flour * Su, pct: p.sugar } : null,
      (p.butter || 0) > 0 ? { name: "Butter", weight: flour * B, pct: p.butter } : null,
    ],
  };
}

/* Simple sourdough: starter added on top, no hydration correction. */
export function computeSimpleSourdough(totalDough: number, p: Percents): DoughResult {
  const H = p.hydration / 100, S = p.salt / 100, O = p.oil / 100;
  const Su = p.sugar / 100, B = (p.butter || 0) / 100, St = p.yeast / 100;
  const flour = totalDough / (1 + H + S + O + Su + B + St);
  return {
    flour,
    rows: [
      { name: "Flour", weight: flour, pct: 100, accent: true },
      { name: "Water", weight: flour * H, pct: p.hydration, accent: true },
      { name: "Starter", weight: flour * St, pct: p.yeast, accent: true },
      { name: "Salt", weight: flour * S, pct: p.salt },
      p.oil > 0 ? { name: "Oil", weight: flour * O, pct: p.oil } : null,
      p.sugar > 0 ? { name: "Sugar", weight: flour * Su, pct: p.sugar } : null,
      (p.butter || 0) > 0 ? { name: "Butter", weight: flour * B, pct: p.butter } : null,
    ],
  };
}
